import React, { useState, useEffect, useCallback } from 'react';
import type { GlobalCursorPosition, GlobalCrosshairCursorProps } from './globalCursor.types';
import './GlobalCrosshairCursor.styles.css';

// * [COMPONENT] Global Tactical Crosshair Reticle Cursor
// ? Hardware-accelerated global cursor tracking across all routes, sections, and interactive elements.
export const GlobalCrosshairCursor: React.FC<GlobalCrosshairCursorProps> = ({
  isHidden = false,
}) => {
  const [pos, setPos] = useState<GlobalCursorPosition | null>(null);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [isInteractive, setIsInteractive] = useState<boolean>(false);
  const [isClicking, setIsClicking] = useState<boolean>(false);
  const [isRadarPingActive, setIsRadarPingActive] = useState<boolean>(false);

  // * Cursor position and interactive element hit tracking
  const handleMouseMove = useCallback((e: MouseEvent) => {
    try {
      setPos({ x: e.clientX, y: e.clientY });
      setIsVisible(true);

      const target = e.target as HTMLElement | null;
      if (target) {
        const interactive = Boolean(
          target.closest('a, button, [role="button"], input, select, textarea, .recruiter-pill, .interactive-card')
        );
        setIsInteractive(interactive);
      }
    } catch {
      // Fail-open quietly without breaking page flow
    }
  }, []);

  const handleMouseDown = useCallback(() => {
    setIsClicking(true);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsClicking(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsVisible(false);
  }, []);

  const handleMouseEnter = useCallback(() => {
    setIsVisible(true);
  }, []);

  // * Bind global window and document mouse listeners + Control key radar ping
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Control' && !e.repeat) {
        setIsRadarPingActive(true);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Control') {
        setIsRadarPingActive(false);
      }
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    window.addEventListener('mousedown', handleMouseDown, { passive: true });
    window.addEventListener('mouseup', handleMouseUp, { passive: true });
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mouseenter', handleMouseEnter);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mouseenter', handleMouseEnter);
    };
  }, [handleMouseMove, handleMouseDown, handleMouseUp, handleMouseLeave, handleMouseEnter]);

  // If no mousemove has occurred yet, keep element unrendered
  if (!pos || isHidden) {
    return null;
  }

  const cursorClassNames = [
    'global-crosshair-cursor',
    !isVisible ? 'cursor-hidden' : '',
    isInteractive ? 'interactive' : '',
    isClicking ? 'clicking' : '',
    isRadarPingActive ? 'radar-ping' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={cursorClassNames}
      style={{
        transform: `translate3d(${pos.x}px, ${pos.y}px, 0)`,
      }}
      aria-hidden="true"
    >
      <div className="global-crosshair-container">
        <div className="crosshair-axis-x" />
        <div className="crosshair-axis-y" />
        <div className="crosshair-ring-outer" />
        <div className="crosshair-ring-inner" />
        <div className="crosshair-bracket top-left" />
        <div className="crosshair-bracket top-right" />
        <div className="crosshair-bracket bottom-left" />
        <div className="crosshair-bracket bottom-right" />
        <div className="crosshair-point" />
      </div>
    </div>
  );
};

export default GlobalCrosshairCursor;
